import { Controller } from "@hotwired/stimulus"

// Plays a sequence of slides, each for its own length, with a pause button.
// Used by the home banner previews (app/views/previews).
//
// Every element with data-reel-target="slide" and data-index="n" belongs to
// slide n, so a word, a photo and a pass can change together. Each fill
// (data-reel-target="fill") is a progress bar that fills over its slide's
// length, and the end of that fill moves to the next slide: pausing the fill
// pauses the whole sequence.
//
// Under prefers-reduced-motion nothing moves: the first slide stays, and the
// bars and the pause button are hidden. Without JavaScript the first slide
// shows, because the others start with opacity-0 in the markup.
export default class extends Controller {
  static targets = ["slide", "fill", "controls", "button", "label", "pauseIcon", "playIcon"]
  static values = { durations: Array } // seconds, one per slide

  connect() {
    this.paused = false
    this.labelTarget.textContent = "Pause"
    this.pauseIconTarget.toggleAttribute("hidden", false)
    this.playIconTarget.toggleAttribute("hidden", true)
    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.onMotionChange = () => this.start()
    this.motion.addEventListener("change", this.onMotionChange)
    this.start()
  }

  disconnect() {
    this.motion.removeEventListener("change", this.onMotionChange)
    this.animation?.cancel()
  }

  start() {
    this.animation?.cancel()
    this.animation = null
    if (this.motion.matches) {
      this.show(0)
      this.fillTargets.forEach((fill) => { fill.style.transform = "scaleX(0)" })
      this.controlsTarget.hidden = true
      this.element.dataset.reelState = "still"
      return
    }
    this.controlsTarget.hidden = false
    this.play(0)
  }

  play(index) {
    this.animation?.cancel()
    this.index = index
    this.show(index)
    this.fillTargets.forEach((fill, n) => { fill.style.transform = n < index ? "scaleX(1)" : "scaleX(0)" })

    const duration = this.durationsValue[index] * 1000
    this.animation = this.fillTargets[index].animate(
      [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
      { duration, easing: "linear", fill: "forwards" }
    )
    this.animation.onfinish = () => this.play((index + 1) % this.durationsValue.length)
    if (this.paused) this.animation.pause()
    this.element.dataset.reelState = this.paused ? "paused" : "playing"
  }

  toggle() {
    if (!this.animation) return
    this.paused = !this.paused
    if (this.paused) {
      this.animation.pause()
    } else {
      this.animation.play()
    }
    this.labelTarget.textContent = this.paused ? "Play" : "Pause"
    this.pauseIconTarget.toggleAttribute("hidden", this.paused)
    this.playIconTarget.toggleAttribute("hidden", !this.paused)
    this.element.dataset.reelState = this.paused ? "paused" : "playing"
  }

  show(index) {
    this.element.dataset.reelIndex = index
    this.slideTargets.forEach((slide) => {
      const active = Number(slide.dataset.index) === index
      slide.classList.toggle("opacity-0", !active)
      slide.classList.toggle("opacity-100", active)
      slide.toggleAttribute("aria-hidden", !active)
    })
  }
}
